import { useEffect, useRef } from "react";
import {
  bigDealAmount,
  catchUpDarkMs,
  catchUpOfflineMs,
  mediumDealAmount,
  personalBestMinAmount,
  recordMinAmount,
  saleConfettiMax,
  saleConfettiMin,
} from "../constants";
import { chargeMs } from "../animations/charge";
import { targetPartyGroup } from "../animations/party";
import { byId, durationOf } from "../animations/registry";
import { readBests, readRecord, writeBests, writeRecord } from "../utils/saleRecord";
import { readLastSeen, touchLastSeen, writeLastSeen } from "../utils/lastSeen";
import { isScreenAwake, onScreenChange } from "../utils/screenAwake";

// Kijkt of er tussen twee keer ophalen iets is bijgekomen en vertaalt dat
// naar animaties. De eerste meting telt niet mee: dat is gewoon de stand
// zoals hij was, niet iets wat zojuist gebeurde.
//
// Behalve na een herstart: dan pakken we de stand van vlak voor de tv
// uitging (zie utils/lastSeen.js) en halen we in wat er sindsdien is
// verkocht. Zie useSaleEvents onderaan.

function snapshot(data) {
  const totals = new Map();
  const order = [];

  (data.rankList || []).forEach((user) => {
    if (!user?.name) return;
    totals.set(user.name, user.totalPrice || 0);
    order.push(user.name);
  });

  // Hetzelfde trucje voor de diensten: daaruit halen we later wát er
  // verkocht is, want de ranglijst weet alleen wie en voor hoeveel.
  const services = new Map();
  (data.soldServices || []).forEach((service) => {
    if (!service?.name) return;
    services.set(service.name, service.totalPrice || 0);
  });

  const target = data.teamTarget?.amount || 0;
  const percent = target
    ? Math.min(100, Math.round(((data.scoredTowardsTarget || 0) / target) * 100))
    : 0;

  return { totals, order, services, amount: data.amount || 0, percent };
}

// Van en naar localStorage: Maps kunnen niet in JSON, dus daar worden het
// gewone objecten. at is het moment van de meting, scope de maand.
function pack(snap, scope) {
  return {
    scope,
    at: snap.at,
    totals: Object.fromEntries(snap.totals),
    order: snap.order,
    services: Object.fromEntries(snap.services),
    amount: snap.amount,
    percent: snap.percent,
  };
}

function unpack(record) {
  return {
    totals: new Map(Object.entries(record.totals || {})),
    order: record.order || [],
    services: new Map(Object.entries(record.services || {})),
    amount: record.amount || 0,
    percent: record.percent || 0,
    at: record.at,
  };
}

// Welke diensten zijn er bij gekomen, en voor hoeveel. Grootste eerst, zodat
// die naast de grootste verkoop komt te liggen.
function soldServices(before, now) {
  const grown = [];
  now.services.forEach((price, name) => {
    const was = before.services.get(name) ?? 0;
    if (price > was) grown.push({ name, amount: price - was });
  });
  return grown.sort((a, b) => b.amount - a.amount);
}

// Zoekt bij een verkoop de dienst die er in dezelfde ronde voor hetzelfde
// bedrag bij is gekomen, en haalt hem uit de lijst: twee verkopen van
// hetzelfde bedrag pakken zo niet dezelfde dienst. Past niets precies, dan
// blijft de dienst leeg - liever geen regel dan de verkeerde dienst.
function takeService(grown, amount) {
  const index = grown.findIndex((service) => service.amount === amount);
  if (index < 0) return null;
  return grown.splice(index, 1)[0].name;
}

// Wie is erbij gekomen of heeft meer op de teller? Grootste deal eerst,
// zodat die de banner krijgt als er meerdere zijn. Met erbij wát er
// verkocht is: is er precies één verkoop en één dienst die opliep, dan
// hoort dat bij elkaar, ook als het bedrag over meerdere regels verdeeld
// staat.
function salesBetween(before, now) {
  const sales = [];
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    if (price > was) sales.push({ name, amount: price - was });
  });
  sales.sort((a, b) => b.amount - a.amount);

  const grown = soldServices(before, now);
  sales.forEach((sale) => {
    sale.service = takeService(grown, sale.amount);
  });
  if (sales.length === 1 && !sales[0].service && grown.length === 1) {
    sales[0].service = grown[0].name;
  }

  return sales;
}

// De grootste stijger in de ranglijst, of null.
function biggestClimber(before, now) {
  let climber = null;
  now.order.forEach((name, index) => {
    const was = before.order.indexOf(name);
    if (was < 0 || was <= index) return;
    const jump = was - index;
    if (!climber || jump > climber.jump) {
      climber = { name, position: index + 1, from: was + 1, jump };
    }
  });
  return climber;
}

// De volgorde van één verkoop, in ms vanaf het begin van het groepje. Eerst
// een seintje: de schermrand licht vijf seconden op, zonder tekst en zonder
// donker beeld. Dat is de aankondiging van de aankondiging - wie in de ruimte
// staat kijkt op, en heeft daarna nog de hele aanloop om zich om te draaien
// voor het echte werk begint.
//
// Dan de aanloop: het beeld gaat donker en er loopt een rij blokjes vol,
// zodat iedereen al kijkt voor er iets te lezen valt. Dan de aankondiging op donker beeld
// (of bij een record het vuurwerk) met confetti eroverheen. Als die weg is
// komt het bord terug en wijst het zelf aan wat er veranderd is: de rij van
// de verkoper licht op en de meter pulseert. Geen balk of extra bui meer
// daarachteraan: wie, wat en hoeveel heb je op de aankondiging al gelezen.
// Het kaartje van de stijger komt pas als de rij klaar is, zodat er nooit
// meer dan één ding tegelijk om aandacht vraagt.
//
// De aankondiging begint terwijl de aanloop nog loopt: zo is het donker van
// die kaart er al voor de aanloop verdwijnt, en licht het scherm daartussen
// niet even op. Die 0,4s is precies hoe lang het donker van de aankondiging
// erover doet om op te komen (fx-splash-dim in animations.css); eerder mag,
// later niet, want dan zit er een gat.
const chargeHandoff = chargeMs - 400;

// Hoe lang het seintje voor de aanloop uit gaat: precies zo lang als de
// schermrand zelf duurt, dus de aanloop begint op het moment dat de rand
// weg is. Zie screen-glow in animations/registry.js.
//
// Vijf seconden en niet korter: wie opkijkt van de rand moet nog de tijd
// hebben om zich om te draaien en naar het scherm te lopen voor de aanloop
// begint.
const glowLead = 5000;
const cardConfettiAfterSplash = 900; // de naam staat er dan
// De salvo's van de zijkanten over de aankondiging: de eerste als het bedrag
// er staat, de rest verdeeld over de tijd dat hij blijft staan.
const cannonsAfterSplash = [1200, 4200, 6800];
// Het donker van de aankondiging is dan net weg en het bord staat weer.
const flashAfterCard = 200;
// De stijger wacht tot de rij uitgelicht is: eerst wie, dan wat dat
// betekent voor de stand.
const climbAfterFlash = byId("row-flash").ms;

// Bij een record: eerst de gewone aankondiging, en als je die gelezen hebt
// slaat de stempel erop, 1,6s voor de recordkaart. De recordkaart begint
// terwijl de aankondiging wegfadet, zodat het donker blijft staan:
// fx-splash-dim gaat in zijn laatste 0,54s weg, en het donker van de
// recordkaart komt in 0,4s op. Beide rekenen vanaf de duur van de
// aankondiging, dus wordt die langer, dan schuiven ze vanzelf mee.
const splashMs = byId("sale-splash").ms;
const recordAfterSplash = splashMs - 600;
const stampAfterSplash = recordAfterSplash - 1600;

// Haalt deze verkoop het target, dan begint de aanloop naar 100 als het bord
// even rustig heeft gestaan: de rij en de stijger zijn dan uitgespeeld, en
// er zit een adempauze tussen de verkoop en het feest. Zonder die pauze
// rolt het ene donkere beeld direct in het volgende en leest het als één
// lange brij. Op confetti en kanonnen wachten we niet: die zijn dan al weg
// of vallen gewoon door de aanloop heen.
const calmBeforeTarget = 1500;

function targetAfter(events) {
  const settled = events.reduce((latest, [id, payload, at]) => {
    if (id === "confetti-burst" || id === "party-cannons") return latest;
    return Math.max(latest, at + durationOf(byId(id), payload));
  }, 0);
  return settled + calmBeforeTarget;
}

// Hoeveel confetti er over de aankondiging valt: de kleinste verkoop krijgt
// saleConfettiMin, vanaf bigDealAmount saleConfettiMax, en daartussen loopt
// het mee met het bedrag, zodat je aan de drukte al ziet of het een grote was.
function confettiFor(amount) {
  const part = Math.min(1, Math.max(0, amount / bigDealAmount));
  return saleConfettiMin + (saleConfettiMax - saleConfettiMin) * part;
}

// Alles wat bij één verkoop hoort, als lijst van [animatie, gegevens, tijd].
// De animatiemotor speelt het als één groepje af, zie useAnimations.
//
// record: het oude record als deze verkoop eroverheen gaat. Dan komt eerst
// gewoon de aankondiging (wie, wat, hoeveel), dan slaat de stempel "NIEUW
// RECORD!" erop, en daarna volgt het vuurwerk met de recordkaart en het oude
// record erbij. In die volgorde leest het als een verhaal: er is iets
// verkocht, en o, het is ook nog een record.
// best: hetzelfde, maar dan het eigen record van deze verkoper, met de
// stempel "EIGEN RECORD!" en de kaart "Eigen record". Er is er hooguit één
// van de twee: het record van het hele bord is ook altijd een eigen record,
// en dan wint het vuurwerk.
// climber: de stijger als dat deze verkoper is, zodat het kaartje bij de
// verkoop hoort in plaats van er los achteraan te komen.
function saleGroup(sale, { record = null, best = null, climber = null } = {}) {
  const beaten = record || best;
  const splashAt = glowLead + chargeHandoff;
  // Wanneer de laatste kaart weg is en het bord weer in beeld staat.
  let boardAt = splashAt + byId("sale-splash").ms;

  const events = [
    ["screen-glow", {}, 0],
    ["sale-charge", {}, glowLead],
    ["sale-splash", sale, splashAt],
  ];

  if (beaten) {
    const recordId = record ? "fireworks" : "personal-best";
    const recordAt = splashAt + recordAfterSplash;
    events.push(
      ["record-stamp", { kind: record ? "record" : "best" }, splashAt + stampAfterSplash],
      [
        recordId,
        {
          name: sale.name,
          amount: sale.amount,
          service: sale.service,
          previous: beaten.amount,
        },
        recordAt,
      ],
    );
    boardAt = recordAt + byId(recordId).ms;
  }

  const flashAt = boardAt + flashAfterCard;
  events.push(
    ["gauge-pulse", {}, flashAt],
    ["row-flash", { name: sale.name }, flashAt],
  );

  // Confetti over de aankondiging heen: bij elke verkoop, ook een kleine,
  // want daar is hij juist voor. Niet bij een record - dan knalt er straks
  // vuurwerk, en de confetti hangt dan nog in de lucht: twee soorten spul
  // door elkaar. Bij een eigen record wel: daar beweegt verder alleen een
  // handvol strepen.
  if (!record) {
    events.push([
      "confetti-burst",
      { count: confettiFor(sale.amount) },
      splashAt + cardConfettiAfterSplash,
    ]);

    // En van de zijkanten: de kanonnen van het feestje, die vanuit beide
    // hoeken onderin over de aankondiging heen schieten. Eén salvo bij elke
    // verkoop, twee vanaf mediumDealAmount, drie vanaf bigDealAmount: net als
    // bij de buien zie je aan het aantal wat voor verkoop het was.
    const salvos = sale.amount >= bigDealAmount ? 3 : sale.amount >= mediumDealAmount ? 2 : 1;
    cannonsAfterSplash
      .slice(0, salvos)
      .forEach((at) => events.push(["party-cannons", {}, splashAt + at]));
  }

  if (climber) {
    const climbAt = flashAt + climbAfterFlash;
    events.push(["rank-up", climber, climbAt]);
    if (climber.position <= 3) events.push(["podium-bounce", {}, climbAt]);
  }

  return events;
}

// Geeft terug wat er moet gebeuren, als lijst van groepjes die de
// animatiemotor na elkaar afspeelt. Elke verkoop is een eigen groepje: twee
// verkopen door elkaar heen leest niemand.
//
// record: de grootste losse verkoop tot nu toe, of null als we die (nog)
// niet kennen. Zonder record geen vuurwerk - de eerste verkoop die we zien
// is niet per se een record, we hebben alleen de geschiedenis niet.
//
// catchUp: inhalen na een herstart. De verkopen zelf staan dan al op de
// kaart "Verkopen gemist" (zie useSaleEvents), dus die krijgen geen eigen
// beurt; alleen de stijger en het target komen er nog achteraan. Geen
// vuurwerk en geen record: de API geeft totalen per verkoper, en wat na een
// nacht als één bedrag binnenkomt kunnen best drie losse verkopen zijn. En
// het target komt als afsluiter, niet in plaats van alles: eerst zien hoe
// het team er kwam, dan het feestje.
//
// Live net zo: eerst de verkoop die het team over de streep trok, met alles
// wat daarbij hoort, en daarna de meter naar 100 en het feest. Zo zie je wie
// het deed voor je ziet dat het gelukt is.
function toGroups(before, now, { record = null, bests = {}, catchUp = false } = {}) {
  const targetHit = now.percent >= 100 && before.percent < 100;
  // Het target is het feestje van de maand: de kaart, de kanonnen en daarna
  // gaat het hele dashboard los, met muziek. Zie targetPartyGroup in
  // animations/party.js.
  const targetGroup = targetPartyGroup(now.amount);


  const sales = catchUp ? [] : salesBetween(before, now);
  const climber = biggestClimber(before, now);
  const groups = [];

  sales.forEach((sale, index) => {
    // Gaat de grootste van deze ronde over het record heen? Dan het
    // vuurwerk, met het oude record erbij zodat je ziet wát er gebroken is.
    const isRecord =
      index === 0 &&
      record &&
      sale.amount > record.amount &&
      sale.amount >= recordMinAmount;

    // En anders: gaat hij over zijn eigen beste verkoop heen? Kennen we die
    // niet, dan gebeurt er niets - dit is dan de eerste verkoop die wij van
    // hem zien, en dat is geen record. Zie utils/saleRecord.js.
    const best = bests[sale.name] || null;
    const isBest =
      !isRecord &&
      best &&
      sale.amount > best.amount &&
      sale.amount >= personalBestMinAmount;

    groups.push(
      saleGroup(sale, {
        record: isRecord ? record : null,
        best: isBest ? best : null,
        climber: climber?.name === sale.name ? climber : null,
      }),
    );
  });

  // De stijger die niet bij een verkoop hierboven hoort krijgt een eigen
  // kaartje.
  if (climber && !sales.some((sale) => sale.name === climber.name)) {
    const group = [["rank-up", climber]];
    if (climber.position <= 3) group.push(["podium-bounce", {}]);
    groups.push(group);
  }

  // Het target komt achter de laatste verkoop aan, in hetzelfde groepje: als
  // los groepje wachtte het eerst tot de laatste snipper geland was en dan
  // nog de pauze tussen twee verkopen, en dat is een halve minuut waarin
  // niemand snapt waarom het feest nog niet begint. Zonder verkoop ervoor
  // (het inhalen na een herstart, of een los kaartje van de stijger) blijft
  // het gewoon een eigen groepje.
  if (targetHit) {
    const last = groups[groups.length - 1];
    const afterSale = last?.some(([id]) => id === "sale-splash");
    if (afterSale) {
      const at = targetAfter(last);
      groups[groups.length - 1] = [
        ...last,
        ...targetGroup.map(([id, payload, start]) => [id, payload, start + at]),
      ];
    } else {
      groups.push(targetGroup);
    }
  }

  // Er is geld bij gekomen maar niemand sprong eruit: dan alleen de meter.
  // Niet bij het inhalen, daar zegt de kaart het al.
  if (!catchUp && groups.length === 0 && now.amount > before.amount) {
    groups.push([["gauge-pulse", {}]]);
  }

  return groups;
}

// Dezelfde vergelijking, maar los te gebruiken: het animatielab past een
// verzonnen verkoop toe op zijn cijfers en vraagt hier wat er dan zou
// gebeuren. Zo test je in het lab precies wat er live draait.
export function diffGroups(
  beforeData,
  nowData,
  record = readRecord(),
  bests = readBests(),
) {
  return toGroups(snapshot(beforeData), snapshot(nowData), { record, bests });
}

// De grootste losse verkoop van deze ronde, of null.
function biggestSale(before, now) {
  let best = null;
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    const amount = price - was;
    if (amount > 0 && (!best || amount > best.amount)) best = { name, amount };
  });
  return best;
}

// scope: verandert die (andere maand), dan begint het vergelijken opnieuw.
// Zonder dat zou het overschakelen naar een andere maand een regen aan
// "nieuwe verkopen" opleveren, terwijl er niets is gebeurd.
export function useSaleEvents(data, playGroup, scope) {
  const previous = useRef(null);
  const scopeRef = useRef(scope);
  // Het record loopt dóór over maanden heen, dus die zetten we niet terug
  // als de scope wisselt.
  const record = useRef(readRecord());
  // En per verkoper zijn eigen grootste. Loopt net als het record door over
  // maanden heen.
  const bests = useRef(readBests());

  // Hoe lang het beeld weg was sinds de vorige vergelijking. Dit is het
  // getal waar het inhalen op afgaat: gemeten donkere tijd, niet hoe lang
  // het ophalen geleden is. Dat laatste is bij een gewone ophaalronde al
  // een minuut, dus daar valt geen korte drempel op te zetten.
  const darkFor = useRef(0);

  // De tv gaat uit terwijl de Pi gewoon doorloopt. Vanaf dat moment schuift
  // de marker niet meer op (zie hieronder), maar de tijd die erin staat
  // moet nog wel bijgesteld: die hoort het moment te zijn waarop het beeld
  // wegviel, niet dat van de laatste ophaalronde ervoor. Anders zegt de
  // kaart straks een paar minuten te vroeg hoe laat het scherm uitging.
  //
  // Bij het aangaan tellen we op hoe lang het donker was. Optellen en niet
  // overschrijven: gaat de tv twee keer kort uit voor we weer een ronde
  // draaien, dan telt dat samen.
  useEffect(
    () =>
      onScreenChange(({ awake, darkAt, sleptMs }) => {
        if (awake) {
          darkFor.current += sleptMs || 0;
          return;
        }
        if (!darkAt) return;
        if (previous.current) previous.current.at = darkAt;
        touchLastSeen(scopeRef.current, darkAt);
      }),
    [],
  );

  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      previous.current = null;
    }

    if (!data) {
      previous.current = null;
      return undefined;
    }

    // Staat deze tv donker (het scherm uit, de Pi aan), dan slaan we deze
    // ronde helemaal over: de marker blijft staan waar hij stond en er
    // wordt niets afgespeeld. Wat er nu binnenkomt hoort straks op de kaart
    // "Verkopen gemist", niet in een animatie die niemand ziet. Zonder dit
    // schuift de marker de hele nacht mee en valt er 's ochtends niets meer
    // in te halen. Zie utils/screenAwake.js.
    if (!isScreenAwake()) return undefined;

    // Hoe lang het beeld weg was sinds de vorige ronde, en meteen weer op
    // nul: deze ronde neemt het mee, de volgende begint schoon.
    const darkMs = darkFor.current;
    darkFor.current = 0;

    const now = { ...snapshot(data), at: Date.now() };

    // Na een herstart weten wij niets meer, maar de tv nog wel: de stand van
    // vlak voor hij uitging staat in localStorage. Die geldt dan als vorige
    // meting. Staat daar niets (eerste keer, of een andere maand), dan is
    // dit gewoon de nulmeting zoals altijd.
    const stored = previous.current ? null : readLastSeen(scope);
    const before = previous.current || (stored ? unpack(stored) : null);
    previous.current = now;
    writeLastSeen(pack(now, scope));
    if (!before) return undefined;

    // Heeft er een tijd niemand meegekeken, dan is dit inhalen: één kaart
    // "Verkopen gemist" met daarop wie wat verkocht heeft (grootste eerst),
    // en daarna alleen nog de stijger en het target. Met keep, want dit
    // hoort er hoe dan ook te komen, ook als de rij vol zit.
    //
    // Twee manieren om daarachter te komen. De tv die uitging hebben we
    // echt gemeten, dus daar is een minuut genoeg. Stond de browser zelf
    // stil (de Pi uit, het tabblad dicht, geen netwerk), dan hebben we
    // alleen het gat in de bewaarde stand, en dat gat is bij een gewone
    // ophaalronde al een minuut: daar moet een ruimere drempel op.
    if (darkMs >= catchUpDarkMs || now.at - before.at >= catchUpOfflineMs) {
      const sales = salesBetween(before, now);
      const amount = sales.reduce((sum, sale) => sum + sale.amount, 0);
      // Ook als er niets verkocht is. De kaart zegt dan hoe lang het scherm
      // uit stond en dat er niets is bijgekomen: dat is óók nieuws, en het
      // is meteen het bewijs dat de tv weer leeft.
      playGroup([["missed-sales", { since: before.at, amount, sales }]], {
        keep: true,
      });
      toGroups(before, now, { catchUp: true }).forEach((group) =>
        playGroup(group, { keep: true }),
      );
      return undefined;
    }

    const groups = toGroups(before, now, {
      record: record.current,
      bests: bests.current,
    });

    // Record bijwerken, ook als er geen animatie bij hoort: kennen we er nog
    // geen, dan is deze verkoop vanaf nu de lat.
    const biggest = biggestSale(before, now);
    if (biggest && biggest.amount > (record.current?.amount ?? 0)) {
      record.current = { ...biggest, at: new Date().toISOString() };
      writeRecord(record.current);
    }

    // En hetzelfde per verkoper. Ook hier: dit gebeurt ná toGroups, want die
    // moest nog naast de oude lat kunnen leggen. Een verkoop onder
    // personalBestMinAmount telt wel gewoon mee - de lat gaat over of we er
    // een kaart van maken, niet over wat iemands beste verkoop is.
    const raised = { ...bests.current };
    let changed = false;
    now.totals.forEach((price, name) => {
      const amount = price - (before.totals.get(name) ?? 0);
      if (amount <= 0 || amount <= (raised[name]?.amount ?? 0)) return;
      raised[name] = { amount, at: new Date().toISOString() };
      changed = true;
    });
    if (changed) {
      bests.current = raised;
      writeBests(raised);
    }

    // Elke verkoop als eigen groepje: de animatiemotor speelt ze na elkaar
    // en zet een volgende verkoop in de rij tot het lopende groepje voorbij
    // is.
    groups.forEach((group) => playGroup(group));

    return undefined;
  }, [data, playGroup, scope]);
}
